// Makes [data-search-trigger] buttons act like clicking the header search bar.
// Focuses the search input of whichever header is showing (main or sticky), so the
// theme's own predictive search loads suggestions as the customer types.
// Usage: add data-search-trigger to a button, or call window.headerSearch.open()
const headerSearch = {
	isDesktop() {
		return window.matchMedia('(min-width:1025px)').matches;
	},
	getInput() {
		// The sticky header is always in the page (invisible until .has-sticky), so pick it only when it's showing
		if (this.isDesktop() && document.body.classList.contains('has-sticky')) {
			const stickyInput = document.querySelector('.hdr-content-sticky .hdr-open-search input[type="search"]');
			if (stickyInput) return stickyInput;
		}
		return [...document.querySelectorAll('.hdr-open-search input[type="search"]')]
			.find(el => !el.closest('.hdr-content-sticky') && el.offsetParent !== null);
	},
	revealHeader(input) {
		const body = document.body;
		if (!this.isDesktop()) {
			// Mobile header slides away on scroll down; bring it back and stop the keyboard scroll hiding it again
			if (body.classList.contains('scroll-down')) {
				body.classList.replace('scroll-down', 'scroll-up');
			}
			body.classList.add('blockSticky');
			setTimeout(() => body.classList.remove('blockSticky'), 1000);
			return;
		}
		if (input.getBoundingClientRect().bottom < 0) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	},
	open(fallbackUrl) {
		const mobileToggle = [...document.querySelectorAll('search-toggle-mobile [data-toggle-search]')].find(el => el.offsetParent !== null);
		if (mobileToggle) {
			// Search bar hidden behind the mobile search icon: the icon opens and focuses it
			if (!mobileToggle.classList.contains('is-open')) mobileToggle.click();
			if (document.activeElement?.matches('input[type="search"]')) searchHandoff.play(document.activeElement);
			return;
		}

		const input = this.getInput();
		if (input) {
			this.revealHeader(input);
			input.focus({ preventScroll: true });
			input.click();
			// Already has text: show its suggestions straight away, the same way typing does
			if (input.value.trim()) input.dispatchEvent(new Event('input', { bubbles: true }));
			searchHandoff.play(input);
			return;
		}

		const searchIcon = document.querySelector('search-popup a');
		if (searchIcon) {
			searchIcon.click();
			return;
		}

		window.location.href = fallbackUrl || window.routes?.search_url || '/search';
	}
};
window.headerSearch = headerSearch;

document.addEventListener('click', (e) => {
	const trigger = e.target.closest('[data-search-trigger]');
	if (!trigger) return;
	e.preventDefault();
	headerSearch.open(trigger.dataset.searchUrl || trigger.getAttribute('href'));
});

// Hand-off animation: a highlight glides from the pressed button to the search bar,
// then a ring pulses around it. Follows the bar live, so it stays on target while
// the page scrolls or the mobile header slides in. Transform/opacity only.
const searchHandoff = {
	lastPress: null,
	layers: [],
	frame: null,
	easeInOut: t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
	easeOut: t => 1 - Math.pow(1 - t, 3),
	createLayer(styles) {
		const el = document.createElement('div');
		el.setAttribute('aria-hidden', 'true');
		el.style.cssText = 'position:fixed;left:0;top:0;pointer-events:none;z-index:2147483000;opacity:0;will-change:transform,opacity;' + styles;
		document.body.appendChild(el);
		this.layers.push(el);
		return el;
	},
	stop() {
		cancelAnimationFrame(this.frame);
		this.layers.forEach(el => el.remove());
		this.layers = [];
	},
	getStartRect() {
		const press = this.lastPress;
		if (press && performance.now() - press.time < 1000 && press.rect.width) return press.rect;
		const active = document.activeElement;
		return active && active !== document.body ? active.getBoundingClientRect() : null;
	},
	play(input) {
		this.stop();
		const target = input.closest('.input-group') || input;
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const rootStyle = getComputedStyle(document.documentElement);
		const yellow = rootStyle.getPropertyValue('--brand-yellow').trim() || '#FFC333';
		const green = rootStyle.getPropertyValue('--brand-green').trim() || '#3D4A3E';
		const radius = parseFloat(getComputedStyle(target).borderRadius) || 8;
		const from = reduceMotion ? null : this.getStartRect();
		const base = target.getBoundingClientRect();
		const pad = 4;

		let ghost = null;
		let flight = 0;
		if (from && base.width && base.height) {
			const distance = Math.hypot(base.left - from.left, base.top - from.top);
			flight = Math.min(Math.max(380 + distance * 0.25, 450), 750);
			ghost = this.createLayer(`width:${base.width}px;height:${base.height}px;transform-origin:0 0;border-radius:${radius}px;background:${yellow};box-shadow:0 6px 24px ${yellow};`);
		}
		const ring = this.createLayer(`width:${base.width + pad * 2}px;height:${base.height + pad * 2}px;transform-origin:50% 50%;border-radius:${radius + pad}px;border:2px solid ${green};box-shadow:0 0 0 3px ${yellow},0 0 18px ${yellow};`);
		const ringStart = flight * 0.65;
		const ringDuration = 900;
		const start = performance.now();

		const tick = now => {
			const elapsed = now - start;
			const rect = target.getBoundingClientRect();

			if (ghost) {
				const t = Math.min(elapsed / flight, 1);
				const e = this.easeInOut(t);
				const x = from.left + (rect.left - from.left) * e;
				const y = from.top + (rect.top - from.top) * e;
				const sx = (from.width + (rect.width - from.width) * e) / base.width;
				const sy = (from.height + (rect.height - from.height) * e) / base.height;
				ghost.style.transform = `translate3d(${x}px,${y}px,0) scale(${sx},${sy})`;
				ghost.style.opacity = 0.45 * (t < 0.15 ? t / 0.15 : t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1);
			}

			const rt = Math.min(Math.max((elapsed - ringStart) / ringDuration, 0), 1);
			const scale = reduceMotion ? 1 : 1.06 - 0.06 * this.easeOut(rt);
			ring.style.transform = `translate3d(${rect.left - pad}px,${rect.top - pad}px,0) scale(${scale})`;
			ring.style.opacity = rt < 0.25 ? rt / 0.25 : 1 - this.easeOut((rt - 0.25) / 0.75);

			if (elapsed < ringStart + ringDuration) {
				this.frame = requestAnimationFrame(tick);
			} else {
				this.stop();
			}
		};
		this.frame = requestAnimationFrame(tick);
	}
};

// Remember the button that was pressed (before popups hide it) as the animation's start point
document.addEventListener('click', (e) => {
	const pressed = e.target.closest('button, a, [role="button"]');
	if (pressed) searchHandoff.lastPress = { rect: pressed.getBoundingClientRect(), time: performance.now() };
}, true);
